/**
 * PreviewFilterMenu — one icon button that holds every preview list control.
 *
 * Replaces four rows of chips that ate the top of the panel before a single
 * row was visible. Collapsed, it is just a sliders icon; when any control is
 * off its default the icon turns primary and carries a count, so a filtered
 * list can never look like the whole list.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal } from 'lucide-react';

import { useModalPortalSurface } from '@/components/ui/layout/ModalPortalSurfaceContext';
import { cn } from '@/lib/utils';

import PreviewFilterMenuPanel from './PreviewFilterMenuPanel';
import { PREVIEW_FILTER_DEFAULTS, activeFilterCount } from './preview-filter-options';
import { useAnchoredMenu } from './use-anchored-menu';
import type { PreviewFilterMenuProps } from './preview-filter-menu.types';

const PANEL_WIDTH = 248;

const PreviewFilterMenu: FC<PreviewFilterMenuProps> = (props) => {
  const menu = useAnchoredMenu(PANEL_WIDTH);
  const surface = useModalPortalSurface();
  const pools = props.pools && props.pools.length >= 2 ? props.pools : undefined;
  const count = activeFilterCount({
    sortMode: props.sortMode,
    rankFilter: props.rankFilter,
    statusFilter: props.statusFilter,
    outcomeFilter: props.outcomeFilter,
    poolFilter: props.poolFilter ?? null,
  });

  const reset = () => {
    const d = PREVIEW_FILTER_DEFAULTS;
    props.onSortModeChange(d.sortMode);
    props.onRankFilterChange(d.rankFilter);
    props.onStatusFilterChange(d.statusFilter);
    props.onOutcomeFilterChange(d.outcomeFilter);
    props.onPoolFilterChange?.(d.poolFilter);
  };

  const panel = (
    <PreviewFilterMenuPanel
      {...props}
      pools={pools}
      ref={menu.menuRef}
      style={{ top: menu.box.top, left: menu.box.left, width: PANEL_WIDTH }}
      onReset={reset}
      activeCount={count}
    />
  );

  return (
    <>
      <button
        ref={menu.triggerRef}
        type="button"
        onClick={menu.toggle}
        aria-haspopup="dialog"
        aria-expanded={menu.open}
        aria-label={count > 0 ? `Filters (${count} active)` : 'Filters'}
        title={count > 0 ? `${count} filter${count > 1 ? 's' : ''} active` : 'Filters'}
        className={cn(
          'inline-flex h-6 items-center gap-1 rounded-md border px-1.5 transition-colors',
          count > 0 || menu.open
            ? 'border-primary/40 bg-primary/10 text-foreground'
            : 'border-border/60 bg-background text-muted-foreground hover:bg-muted/40',
        )}
      >
        <SlidersHorizontal className="h-3 w-3" aria-hidden />
        {count > 0 ? (
          <span className="text-[10px] font-medium tabular-nums">{count}</span>
        ) : null}
      </button>
      {menu.open ? createPortal(panel, surface ?? document.body) : null}
    </>
  );
};

export default PreviewFilterMenu;
